/*
 * Javier Quevedo-Fernandez
 * Copyright Ranj Games. 2013
 */

import BackgroundOverlayStyle from './BackgroundOverlayStyle';
import ScreenNavigatorEvents from './ScreenNavigatorEvents';
import AnimationFactory from './animation/AnimationFactory';
import PopUpInfo from './PopUpInfo';
import { isNull } from '../util/TypeUtil';
import ScreenInformation from './ScreenInformation';
import '../util/Array';

/* ScreenNavigator is the main navigation class
 * It manages a stack of screens and a collection of (prioritized popUps)
 * 
 * @constructor
 * @param {type} contextView
 * @returns {ScreenNavigator}
 */
export default class ScreenNavigator {
    /**
     * 
     * @param {*} contextView 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {AnimationFactory} animationFactory 
     */
    constructor(contextView, dispatcher, animationFactory = null) {
        this.animationFactory = animationFactory || new AnimationFactory();
        // createjs.Container does not have a width therefore we pass the size 
        // to the screen navigator
        this.contextView = contextView;
        this.screenStack = [];
        this.popUps = [];
        this.popUpInfos = {};
        this.screenLayer = new createjs.Container();
        this.contextView.addChild(this.screenLayer);
        this.popUpLayer = new createjs.Container();
        this.contextView.addChild(this.popUpLayer);
        this.defaultOverlayStyle = new BackgroundOverlayStyle('#000', 0.5, 2);
        this.screenWidth = 0;
        this.screenHeight = 0;
        this.overlay = new createjs.Shape();

        // Make sure the overlay is non click through.
        this.overlay.addEventListener("click", () => { });

        this.dispatcher = dispatcher;
    };

    /*
     * Returns the context view of the Screen Navigator
     * @returns {Container} 
     */
    getContextView() {
        return this.contextView;
    };


    /*
     * Sets the contextView of the Screen Navigator
     * @param {Container} contextView
     * 
     */
    setContextView(contextView) {
        this.contextView = contextView;

        // Move screen and popUpLayer over to new contextView
        this.contextView.addChild(this.screenLayer);
        this.contextView.addChild(this.popUpLayer);
    };

    /*
     * 
     * @returns {@pro;width@this._contextView}
     */
    getScreenWidth() {
        return this.screenWidth;
    };
    /*
     * 
     * @returns {@pro;height@this._contextView}
     */
    getScreenHeight() {
        return this.screenHeight;
    };
    /*
     * 
     * @param {type} width
     * @param {type} height
     * @returns {undefined}
     */
    setScreenDimensions(width, height) {
        this.screenWidth = width;
        this.screenHeight = height;
        //TODO: redraw existing overlays, reposition existing popups
        //    console.info("ScreenNavigator.setScreenDimensions, TODO: adjust existing popups and overlays");
        this.updateOverlay();
    };


    /*
     * Returns the original width of the ScreenNavigator
     * This is the width before any resizing and can be used for responsiveness.
     * @returns {Number}
     */
    getOriginalScreenWidth() {
        return this.screenWidthOrig;
    };
    /*
     * Returns the original height of the ScreenNavigator
     * This is the width before any resizing and can be used for responsiveness.
     * @returns {Number}
     */
    getOriginalScreenHeight() {
        return this.screenHeightOrig;
    };
    /*
     * Method setOriginalScreenDimensions should be called during init.
     * Original width and height will be used for responsiveness.
     * @param {Number} width
     * @param {Number} height
     */
    setOriginalScreenDimensions(width, height) {
        this.screenWidthOrig = width;
        this.screenHeightOrig = height;
    };

    /*
     * Updates the main view so that only the screens that should be shown
     * are actually shown
     * 
     * @discussion
     * The screenStack contains the stack of existing screens
     * If one screen hides its parent, all of the super screens
     * of its parents should also be hidden.
     * 
     * @returns {undefined}
     */
    updateScreens() {

        // The ScreenNavigator has to keep track of which new items hit or leave
        // the stage so that it can notify the listener about this
        // (since SomaJS unlike Robotlegs does not create/destroy 
        // the mediators automatically)
        // 

        var oldScreens = [];
        for (var i = 0; i < this.screenLayer.children.length; i++) {
            oldScreens.push(this.screenLayer.children[i]);
        }

        // This is where we actually add and remove the objects from the stage
        this.screenLayer.removeAllChildren();

        for (var i = this.screenStack.length - 1; i >= 0; i--) {
            var screenInfo = this.screenStack[i];
            var screen = screenInfo.screen;
            this.screenLayer.addChildAt(screen, 0);
            if (!screenInfo.keepParentOnStage)
                break;
        }

        // We check which screens were added/removed from the stage and dispatch
        // the corresponding events
        // TODO: Refactor to check for the child difference instead
        //var screensAdded = oldScreens.diff(this.screenLayer.children);
        if (this.dispatcher) {
            for (var i = 0; i < oldScreens.length; i++) {
                var child = oldScreens[i];
                if (!this.screenLayer.children.contains(child)) {
                    this.dispatcher.dispatch(ScreenNavigatorEvents.REMOVED_FROM_STAGE, { data: child });
                }
            }
            for (var i = 0; i < this.screenLayer.children.length; i++) {
                var child = this.screenLayer.children[i];
                if (!oldScreens.contains(child)) {
                    this.dispatcher.dispatch(ScreenNavigatorEvents.ADDED_TO_STAGE, { data: child });
                }
            }
        }

    };

    /*
     * Opens a Screen
     * @param {Container} screen
     * @param {Bool} keepParentOnStage
     * @returns {undefined}
     */

    openScreen(screen, keepParentOnStage, openUnderCurrentScreen, animationType, animationOptions) {
        if (typeof (keepParentOnStage) === 'undefined')
            keepParentOnStage = false;
        if (typeof (openUnderCurrentScreen) === 'undefined')
            openUnderCurrentScreen = false;

        var screenInfo = new ScreenInformation(screen, keepParentOnStage);

        if (openUnderCurrentScreen && this.screenStack.length > 0)
            this.screenStack.splice(this.screenStack.length - 1, 0, screenInfo);
        else
            this.screenStack.push(screenInfo);

        this.updateScreens();

        // Handle animation for new screen
        var animation = this.animationFactory.getAnimationByType(animationType);
        if (animation)
            animation.animate(this, screen, animationOptions);
    };

    /*
     * Navigates to the given screen. The current screen is removed from the stage.
     * @param {Container} screen
     * @returns {undefined}
     */
    gotoScreen(screen, animationType, animationOptions) {
        var keepParentOnStage = false;
        if (this.screenStack.length > 0) {
            var screenInfo = this.screenStack.pop();
            keepParentOnStage = screenInfo.keepParentOnStage;
        }

        this.openScreen(screen, keepParentOnStage, false, animationType, animationOptions);
    };


    /*
     * Closes a screen. The Navigator then navigates to the parent screen on the stack
     * @pre Screen must be on top of the stack
     * @param {Container} screen
     * @returns {undefined}
     */
    closeScreen(screen, animationType, animationOptions) {
        var screenInfo = this.getScreenInfo(screen);
        if (screenInfo) {
            // Handle animation for closing screen
            var animation = this.animationFactory.getAnimationByType(animationType);
            if (animation) {
                animation.animate(this, screen, animationOptions)
                    .then(function () {
                        this.screenStack.splice(this.screenStack.indexOf(screenInfo), 1);
                        this.updateScreens();
                    }.bind(this));
            } else {
                this.screenStack.splice(this.screenStack.indexOf(screenInfo), 1);
                this.updateScreens();
            }
        }
    };


    /*
     * Returns the screen Information for a particular screen
     * Returns null if the screen has no information associated with it
     * @private
     * @param {Container} screen
     * @returns {ScreenNavigator.prototype.screenInfo.screenInfo}
     */
    getScreenInfo(screen) {
        for (var i = 0; i < this.screenStack.length; i++) {
            var screenInfo = this.screenStack[i];
            if (screenInfo.screen === screen)
                return screenInfo;
        }
        return null;
    };

    getParentScreen(screen) {
        var screenInfo = this.getScreenInfo(screen);
        if (screeInfo) {
            if (this.screenStack.indexOf(screenInfo) > 0) {
                return this.screenStack[this.screenStack.indexOf[screenInfo] - 1];
            }
        }
        return null;
    };

    /*
     * Returns the child screen of a given screen
     * @param {Container} screen
     * @returns {Container}
     */
    getChildScreen(screen) {
        var screenInfo = this.getScreenInfo(screen);
        if (screenInfo) {
            var index = this.screenStack.indexOf(screenInfo);
            if (index < this.screenStack.length - 1) {
                var childScreenInfo = this.screenStack[index + 1];
                return childScreenInfo.screen;
            }
        }
        return null;
    };


    /*
     * Closes and removes all of the screens in the stack, navigating to the root context
     * @returns {undefined}
     */
    closeAllScreens() {
        this.screenStack = [];
        this.updateScreens();
    };


    /*
     * Removes a particular screen from the stage
     * @private
     * @param {Container} screen
     * @returns {undefined}
     */
    removeScreen(screen) {
        if (this.screenLayer.contains(screen)) {
            this.screenLayer.removeChild(screen);
        }
    };


    /*
     * Returns true if the given screen is currently being displayed
     * @param {Screen} screen
     * @returns {Bool}
     */
    isDisplayingScreen(screen) {
        return this.screenLayer.contains(screen);
    };

    /*
     * Returs true when a given screen is part of the view stack,
     * independently of the fact that it is being being displayed or not
     * @param {type} screen
     * @returns {Boolean}
     */
    containsScreen(screen) {
        return this.getScreenInfo(screen) ? true : false;

    };


    /*
     * 
     * @param {Container} screen
     * @returns {undefined}
     */
    displayScreen(screen) {
        if (!this.screenLayer.contains(screen))
            this.screenLayer.addChild(screen);
        this.currentScreen = screen;

    };

    /*
     * Opens a PopUp
     * 
     * @param {type} popUp
     * @param {type} backgroundOverlay
     * @param {type} priority
     * @param {type} autoCenter
     * @param {type} customOverlayStyle
     */
    openPopUp(popUp, needsOverlay, priority, autoCenter, customOverlayStyle, animationType, animationOptions) {

        if (typeof (needsOverlay) === 'undefined') needsOverlay = true;
        if (typeof (priority) === 'undefined') priority = 1;
        if (typeof (autoCenter) === 'undefined') autoCenter = false;
        if (typeof (customOverlayStyle) === 'undefined') customOverlayStyle = null;

        var newPopUp;
        var newPopUpInfo;
        var popUpInfo;
        var index;
        var added = false;
        newPopUp = popUp;
        if (autoCenter) {
            popUp.x = Math.round(this.getScreenWidth() / 2 - popUp.nominalBounds.width / 2);
            popUp.y = Math.round(this.getScreenHeight() / 2 - popUp.nominalBounds.height / 2);
        }

        newPopUpInfo = new PopUpInfo(needsOverlay, priority, customOverlayStyle);
        this.popUpInfos[newPopUp.id] = newPopUpInfo;

        if (this.popUps.length > 0) {
            for (index = this.popUps.length - 1; index >= 0; index--) {
                popUp = this.popUps[index];
                popUpInfo = this.popUpInfos[popUp.id];
                if (newPopUpInfo.priority >= popUpInfo.priority) {
                    this.popUps.insert(index + 1, newPopUp);
                    this.popUpLayer.addChildAt(newPopUp, this.popUpLayer.getChildIndex(popUp) + 1);
                    added = true;
                    break;
                }
            }
            if (!added) {
                this.popUpLayer.addChildAt(newPopUp, this.popUpLayer.getChildIndex(this.popUps[0]));
                this.popUps.insert(0, newPopUp);
            }
        }
        else {
            this.popUps.push(newPopUp);
            this.popUpLayer.addChild(newPopUp);
        }
        if (!isNull(this.dispatcher))
            this.dispatcher.dispatch(ScreenNavigatorEvents.ADDED_TO_STAGE, { data: newPopUp });
        this.updateOverlay();

        // Handle animation for new popup
        var animation = this.animationFactory.getAnimationByType(animationType);
        if (animation)
            animation.animate(this, newPopUp, animationOptions);
    };

    /*
     * Closes the provided popUp
     * @param {type} popUp
     */
    closePopUp(popUp, animationType, animationOptions) {
        // Close pop up logic, which should be executed after an animation has finished if there is one
        var postAnimationAction = function () {
            if (this.popUpLayer.contains(popUp)) {
                this.popUpLayer.removeChild(popUp);
            }

            var idx = this.popUps.indexOf(popUp);
            if (idx > -1) this.popUps.splice(this.popUps.indexOf(popUp), 1);
            delete this.popUpInfos[popUp.id];
            if (!isNull(this.dispatcher))
                this.dispatcher.dispatch(ScreenNavigatorEvents.REMOVED_FROM_STAGE, { data: popUp });
            this.updateOverlay();
        }.bind(this);

        // Handle animation for the closing popup
        var animation = this.animationFactory.getAnimationByType(animationType);
        if (animation)
            animation.animate(this, popUp, animationOptions)
                .then(postAnimationAction);
        else
            postAnimationAction.call(this);
    };

    /*
     * Closes all PopUps that were added
     */
    closeAllPopUps() {
        while (this.popUpLayer.numChildren > 0) {
            var popUp = this.popUpLayer.getChildAt(0);
            delete this.popUpInfos[popUp.id];
            this.popUpLayer.removeChild(popUp);
            if (!isNull(this.dispatcher))
                this.dispatcher.dispatch(ScreenNavigatorEvents.REMOVED_FROM_STAGE, { data: popUp });
        }
        this.popUps.splice(0, this.popUps.length);
        this.updateOverlay();
    };


    /*
     * Updates the overlay
     * @private
     * @returns {undefined}
     */

    updateOverlay() {
        var index;
        var popUp;
        var popUpInfo;
        var overlayStyle;
        if (this.popUpLayer.contains(this.overlay)) {
            this.popUpLayer.removeChild(this.overlay);
        }

        for (index = this.popUps.length - 1; index >= 0; index--) {
            popUp = this.popUps[index];
            popUpInfo = this.popUpInfos[popUp.id];
            if (popUpInfo.needsOverlay) {
                overlayStyle = (popUpInfo.overlayStyle === null) ? this.defaultOverlayStyle : popUpInfo.overlayStyle;
                this.drawOverlay(overlayStyle);
                this.popUpLayer.addChildAt(this.overlay, this.popUpLayer.getChildIndex(popUp));
                break;
            }
        }
    };


    /* Draws an overlay of a particular Style
     * @param {BackgroundOverlayStyle} overlayStyle
     */
    drawOverlay(overlayStyle) {
        this.overlay.graphics.clear();

        const overlap = overlayStyle.overlap || 0;

        this.overlay.alpha = overlayStyle.alpha;
        this.overlay.graphics.beginFill(overlayStyle.color).drawRect(-this.getScreenWidth() * .5 - overlap * .5, -this.getScreenHeight() * .5 - overlap * .5, this.getScreenWidth() + overlap, this.getScreenHeight() + overlap);
        this.overlay.x = 0;
        this.overlay.y = 0;
    };
}